import React, { useState } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { isEmpty, keys } from 'lodash-es';

import Button from '../../../components/Buttons/Button';
import InputText from '../../../components/Inputs/InputText';
import { validateEmail } from '../../../utils/helpers';

const fieldsErrors = {
  first_name: 'Please provide a first name',
  last_name: 'Please provide a last name',
  email: 'Please provide a valid email address',
  company_name: 'Please provide a publication or outlet',
};

const validateField = (name, value) => {
  if (!value || !value.trim()) return fieldsErrors[name];
  if (name === 'email' && !validateEmail(value)) return fieldsErrors.email;
  return '';
};

const PressRegisterForm = ({ processing, responseError, bannerError, onSubmit }) => {
  const [values, setValues] = useState({
    first_name: '',
    last_name: '',
    email: '',
    company_name: '',
  });
  const [error, setError] = useState({});

  const handleChange = (name) => (value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    if (error[name]) {
      setError((prev) => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleBlur = (name) => () => {
    setError((prev) => ({ ...prev, [name]: validateField(name, values[name]) }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const nextError = keys(values).reduce((acc, name) => {
      const message = validateField(name, values[name]);
      if (message) acc[name] = message;
      return acc;
    }, {});

    setError(nextError);
    if (!isEmpty(nextError)) return;

    onSubmit(values);
  };

  const fieldError = (name) => error[name] || responseError[name];

  const renderField = ({ name, label, placeholder, type = 'text' }) => {
    const message = fieldError(name);
    return (
      <fieldset
        className={cx('press-register-form__field', {
          'press-register-form__field--invalid': !!message,
        })}
      >
        <label className="press-register-form__label" htmlFor={`press-${name}`}>
          {label}
        </label>
        <InputText
          id={`press-${name}`}
          size="lg"
          name={name}
          type={type}
          value={values[name]}
          placeholder={placeholder}
          isInvalid={!!message}
          onChange={handleChange(name)}
          onBlur={handleBlur(name)}
        />
        {message && <p className="press-register-form__error">{message}</p>}
      </fieldset>
    );
  };

  return (
    <form className="press-register-form" noValidate onSubmit={handleSubmit}>
      {!isEmpty(bannerError) && (
        <div className="press-register-form__banner">
          {bannerError.map((msg) => (
            <p key={msg}>{msg}</p>
          ))}
        </div>
      )}
      <div className="press-register-form__row">
        {renderField({ name: 'first_name', label: 'First Name', placeholder: 'Jane' })}
        {renderField({ name: 'last_name', label: 'Last Name', placeholder: 'Smith' })}
      </div>
      {renderField({
        name: 'email',
        label: 'Work Email',
        placeholder: 'name@company.com',
        type: 'email',
      })}
      {renderField({
        name: 'company_name',
        label: 'Publication or Outlet',
        placeholder: 'e.g. TechCrunch, Bloomberg, Forbes',
      })}
      <Button
        type="submit"
        size="lg"
        className="press-register-form__submit"
        loading={processing}
        disabled={processing}
        onClick={handleSubmit}
      >
        Submit Request
      </Button>
    </form>
  );
};

PressRegisterForm.propTypes = {
  processing: T.bool.isRequired,
  responseError: T.object,
  bannerError: T.array,
  onSubmit: T.func.isRequired,
};

PressRegisterForm.defaultProps = {
  responseError: {},
  bannerError: [],
};

export default PressRegisterForm;
